import imageCompression from 'browser-image-compression'

/**
 * Maximum size (in bytes) accepted for an image upload, enforced *before* compression is
 * attempted. Mirrors the shared upload limit used across the app (logo, poster/flyer, etc.).
 */
export const MAX_IMAGE_UPLOAD_BYTES = 10 * 1024 * 1024

/** Files larger than this are run through {@link compressImageFile} before upload. */
const COMPRESSION_TARGET_MB = 2

/**
 * True when the given `File`'s MIME type indicates an image (`image/*`), excluding
 * `image/svg+xml`. SVG is XML that can embed `<script>`/event-handler payloads, so it is treated
 * as a non-image upload here to prevent stored-XSS via logo/poster uploads.
 */
export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/') && file.type !== 'image/svg+xml'
}

/**
 * Validates an image upload's type and size. Returns an error message (for display to the
 * user) when the file is rejected, or `null` when it's acceptable.
 */
export function validateImageUpload(
  file: File,
  messages: { notImage: string; tooLarge: string }
): string | null {
  if (!isImageFile(file)) return messages.notImage
  if (file.size > MAX_IMAGE_UPLOAD_BYTES) return messages.tooLarge
  return null
}

/**
 * `browser-image-compression` often returns a plain {@link Blob} with a non-standard `.name`
 * property rather than a real {@link File}. {@link FormData.append} only reads the filename from
 * actual `File` instances — for Blobs it falls back to the literal name `"blob"`. Always wrap
 * so callers get a proper `File` that keeps {@link file.name}.
 */
function asNamedFile(blob: Blob, fileName: string, fallbackType: string): File {
  if (blob instanceof File && blob.name === fileName) return blob
  return new File([blob], fileName, {
    type: blob.type || fallbackType,
    lastModified: blob instanceof File ? blob.lastModified : Date.now(),
  })
}

/**
 * Compresses an image file client-side when it's above {@link COMPRESSION_TARGET_MB}, using the
 * same `browser-image-compression` mechanism used for uploads elsewhere (see
 * `qms-international/qms-trace`). SVGs and already-small files are returned unchanged — SVG is
 * vector data that the raster compressor can't meaningfully shrink.
 *
 * Always returns a real {@link File} preserving the original file name (never a nameless Blob
 * that would upload as `"blob"`).
 */
export async function compressImageFile(file: File): Promise<File> {
  if (file.type === 'image/svg+xml') return file
  if (file.size / 1024 / 1024 <= COMPRESSION_TARGET_MB) return file

  try {
    const compressed = await imageCompression(file, {
      maxSizeMB: COMPRESSION_TARGET_MB,
      maxWidthOrHeight: 2560,
      useWebWorker: true,
    })
    return asNamedFile(compressed, file.name, file.type)
  } catch {
    // Compression is a best-effort optimization — if it fails for any reason, fall back to the
    // original file rather than blocking the upload.
    return file
  }
}
